/**
 * The collection book (D290): every breed, building tier, festival mark, season and visitor the
 * player has found, kept for good in `permanent.collection`, across festivals and generations.
 *
 * Noted on every advance, check-in, action and claim by `noteIdleCollection`, which is pure and
 * returns the same state when nothing is new. Each list keeps content order, so noting in one step
 * or many gives the same record. Nothing that earns reads it.
 */
import type { IdleContent } from '@hedgerow/data';
import { idleDecorationOwned } from './decorations.ts';
import { idleYearProgress } from './living.ts';
import type { IdleCollectionState, IdleWorldState } from './state.ts';

export type IdleCollectionKind =
  | 'breeds'
  | 'tiers'
  | 'marks'
  | 'monuments'
  | 'seasons'
  | 'visitors'
  | 'prizes'
  | 'seasonal'
  | 'rosettes'
  | 'sets';

/**
 * D327: once every kind of festival mark stands, further festivals gather into monument
 * grounds of up to this many memorials each, in this order of style, round and round. Kept in
 * step with `MEMORIAL` in `apps/web/src/idle/appearance.ts` by hand: the count and the order,
 * not the look, which lives there alone.
 */
const MONUMENT_PER_GROUND = 6;

export interface IdleCollectionItem {
  kind: IdleCollectionKind;
  /** A content id: stable, so a found item stays found. */
  id: string;
  /** Its name in the content, where the content has one (festival marks have none there). */
  name?: string;
  found: boolean;
}

export interface IdleCollectionView {
  /** Every item, found or not, by kind (breeds, tiers, marks, seasons, visitors, prizes), in content order. */
  items: IdleCollectionItem[];
  found: number;
  total: number;
}

const KINDS: IdleCollectionKind[] = [
  'breeds',
  'tiers',
  'marks',
  'monuments',
  'seasons',
  'visitors',
  'prizes',
  'seasonal',
  'rosettes',
  'sets',
];

/** D348: whether the theme's book lists this category (`collection.categories`). */
function listed(content: IdleContent, k: IdleCollectionKind): boolean {
  return content.collection.categories.includes(k);
}

/** Milestones a producer has reached: its tier (D268). */
function tierOf(content: IdleContent, id: string, owned: number): number {
  const p = content.producers.find((q) => q.id === id);
  return (p?.milestones ?? []).filter((m) => m.owned <= owned).length;
}

/** Every item the collection lists, by kind, in content order. */
function catalogue(
  content: IdleContent,
): Record<IdleCollectionKind, { id: string; name?: string }[]> {
  const unique = (ids: string[]) => [...new Set(ids)].map((id) => ({ id }));
  return {
    breeds: content.collection.breeds.map((b) => ({ id: b.id, name: b.name })),
    tiers: content.collection.tiers.map((t) => ({ id: t.id, name: t.name })),
    // D330: a plain name by id, never the raw id, for a mark the data does not otherwise name.
    marks: unique(content.festival.marks).map((m) => ({
      id: m.id,
      name: content.collection.markNames[m.id] ?? m.id.replace(/_/g, ' '),
    })),
    // D327: the monument-ground styles, named in the data, taken in turn once the marks run out.
    monuments: content.collection.monuments.map((m) => ({ id: m.id, name: m.name })),
    seasons: content.seasons.list.map((s) => ({ id: s.id, name: s.name })),
    visitors: content.bonuses.visitors.kinds.map((v) => ({ id: v.id, name: v.name })),
    // D304: a weekly event's prize, to be had only in its week.
    prizes: content.events.map((e) => ({ id: e.prize.id, name: e.prize.name })),
    // D331: a decoration good for one weekly event only, its own long-tail entry.
    seasonal: content.decorations
      .filter((d) => d.event !== undefined)
      .map((d) => ({ id: d.id, name: d.name })),
    // D326: named here; nothing earns one yet (a hook for the rosette work to come).
    rosettes: content.collection.rosettes.map((r) => ({ id: r.id, name: r.name })),
    // D331: a decoration set is one whole kind, every id of it in the data; kinds come from
    // the data, not a fixed list, so a kind renamed or replaced (scarecrows for workers'
    // clothing) still finds the right set.
    sets: decorationKinds(content).map((k) => ({ id: k, name: `${setName(k)} set` })),
  };
}

/** Every decoration kind in the data, in the order it first appears there. */
function decorationKinds(content: IdleContent): string[] {
  return [...new Set(content.decorations.map((d) => d.kind))];
}

/** A decoration kind's id as a word: `building` -> `Building`. */
function setName(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, ' ');
}

/** What the world shows at `now`, by kind: the items it has in front of the player. */
function inSight(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): Record<IdleCollectionKind, Set<string>> {
  const tiers = Object.entries(state.year.producers).map(([id, ps]) => ({
    id,
    tier: tierOf(content, id, ps.owned),
  }));
  const top = tiers.reduce((a, t) => Math.max(a, t.tier), -1);
  const breeds = content.collection.breeds.filter((b) =>
    tiers.some((t) => t.id === b.producer && t.tier >= b.tier),
  );
  const progress = idleYearProgress(state, content, now);
  // D327: once every kind of mark stands, the next festivals gather on monument grounds, the
  // nth ground taking the style at that turn; a style is found once a ground has reached it.
  const marksLen = content.festival.marks.length;
  const extra = state.permanent.festivals.length - marksLen;
  const grounds = extra > 0 ? Math.floor((extra - 1) / MONUMENT_PER_GROUND) + 1 : 0;
  const styles = content.collection.monuments;
  const monuments = new Set<string>();
  for (let g = 0; g < grounds && styles.length > 0; g++)
    monuments.add(styles[g % styles.length]!.id);
  return {
    breeds: new Set(breeds.map((b) => b.id)),
    tiers: new Set(content.collection.tiers.filter((t) => top >= t.tier).map((t) => t.id)),
    marks: new Set(state.permanent.festivals.map((f) => f.mark)),
    monuments,
    // Every season the year has passed through: the year only moves forward.
    seasons: new Set(content.seasons.list.filter((s) => progress >= s.from).map((s) => s.id)),
    visitors: new Set<string>(),
    prizes: new Set<string>(),
    // D331: a seasonal decoration bought is a long-tail entry of its own.
    seasonal: new Set(
      content.decorations
        .filter((d) => d.event !== undefined && idleDecorationOwned(state, content, d.id))
        .map((d) => d.id),
    ),
    // D326: a hook. Nothing writes `state.permanent.rosettes` yet, so this stays empty until
    // the rosette work lands; once it does, it needs only to set that field.
    rosettes: new Set(state.permanent.rosettes ?? []),
    // D331: a set (one whole decoration kind) is found once every id of that kind is owned,
    // the default included.
    sets: new Set(
      decorationKinds(content).filter((k) =>
        content.decorations
          .filter((d) => d.kind === k)
          .every((d) => idleDecorationOwned(state, content, d.id)),
      ),
    ),
  };
}

/**
 * Note what the world has found at `now`, `visitor` if one has just been met and `prize` if a weekly
 * event's prize has just been won (D304). Returns the same state when nothing is new.
 */
export function noteIdleCollection(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
  visitor?: string,
  prize?: string,
): IdleWorldState {
  const had = state.permanent.collection;
  const sight = inSight(state, content, now);
  if (visitor) sight.visitors.add(visitor);
  if (prize) sight.prizes.add(prize);
  const all = catalogue(content);
  let changed = !had;
  const next = {} as IdleCollectionState;
  // D348: a category the theme does not list is kept as it was and never added to.
  for (const k of KINDS) {
    if (!listed(content, k)) {
      next[k] = had?.[k] ?? [];
      continue;
    }
    const before = new Set(had?.[k] ?? []);
    const list = all[k].map((i) => i.id).filter((id) => before.has(id) || sight[k].has(id));
    // Ids no longer in the content are kept, after the rest.
    for (const id of had?.[k] ?? []) if (!list.includes(id)) list.push(id);
    if (list.length !== (had?.[k]?.length ?? 0)) changed = true;
    next[k] = list;
  }
  if (!changed) return state;
  return { ...state, permanent: { ...state.permanent, collection: next } };
}

/** The collection book (D290): every item, found or not, and how many are found of the total. */
export function idleCollection(state: IdleWorldState, content: IdleContent): IdleCollectionView {
  const kept = noteIdleCollection(state, content, state.advancedTo).permanent.collection;
  const all = catalogue(content);
  const items: IdleCollectionItem[] = [];
  for (const k of content.collection.categories)
    for (const i of all[k]) items.push({ kind: k, ...i, found: !!kept?.[k]?.includes(i.id) });
  const found = items.filter((i) => i.found).length;
  return { items, found, total: items.length };
}
